import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ProjectBoard from '../components/ProjectBoard';
import ProjectGantt from '../components/ProjectGantt';
import ProjectCalendar from '../components/ProjectCalendar';
import ProjectList from '../components/project/ProjectList';
import Dropdown from '../components/project/Dropdown';
import ProjectGallery from '../components/project/ProjectGallery';
import TaskPeek from '../components/project/TaskPeek';
import ProjectTable from '../components/project/ProjectTable';
import ProjectViewTabs from '../components/project/ProjectViewTabs';
import ViewToolbar from '../components/project/ViewToolbar';
import ProjectHeader from '../components/project/ProjectHeader';
import BulkBar from '../components/project/BulkBar';
import NewTaskSplit from '../components/project/NewTaskSplit';
import TrashDialog from '../components/project/TrashDialog';
import ImportCsvDialog from '../components/project/ImportCsvDialog';
import ColumnMenuHost from '../components/project/ColumnMenuHost';
import ColumnMenuButton from '../components/project/ColumnMenuButton';
import {
  getProject,
  updateProject,
  deleteProject,
  duplicateProject,
  getProjects,
  setProjectFavorite,
  createProjectColumn,
  updateProjectColumn,
  deleteProjectColumn,
  duplicateProjectColumn,
  createProjectTask,
  updateProjectTask,
  deleteProjectTask,
  restoreProjectTask,
  duplicateProjectTask,
  reorderProjectTasks,
  bulkProjectTasks,
  pasteProjectTasks,
  createProjectTaskTemplate,
  renameProjectTaskTemplate,
  deleteProjectTaskTemplate,
  createProjectView,
  updateProjectView,
  deleteProjectView,
  getUsers,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { getProjectMenu, setProjectMenu } from '../projectMenu';
import { createHistory } from '../utils/history';
import { toCsv, downloadText } from '../utils/csv';
import { buildExportRows } from '../utils/projectCsv';
import {
  orderedColumns,
  allColumnsInOrder,
  personIds,
  descendantIds,
  TICKETS_COLUMN,
  GROUPABLE_TYPES,
  applyFilter,
  applySearch,
  applySorts,
  groupTasks,
  opsForColumn,
  ticketsPseudoColumn,
} from '../utils/projectView';

const OPTION_TYPES = ['select', 'status', 'multi_select'];

const storedViewKey = (projectId) => `ticket.projectView.${projectId}`;
const readStoredView = (projectId) => {
  try {
    return Number(localStorage.getItem(storedViewKey(projectId))) || null;
  } catch (e) {
    return null;
  }
};
const writeStoredView = (projectId, viewId) => {
  try {
    localStorage.setItem(storedViewKey(projectId), String(viewId));
  } catch (e) {
    // senza localStorage la vista scelta non viene ricordata
  }
};

function ProjectDetail({ user, onLogout }) {
  const { t, lang } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = Number(id);

  const [project, setProject] = useState(null);
  const [columns, setColumns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [focusTaskId, setFocusTaskId] = useState(null);
  const [boardMenu, setBoardMenu] = useState(false);
  const [forceOpen, setForceOpen] = useState(null);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [trashOpen, setTrashOpen] = useState(false);
  const [importState, setImportState] = useState(null); // {text, name}
  const [notice, setNotice] = useState(null); // {text, undo}
  const lastSelected = useRef(null);
  const location = useLocation();

  // Salvataggio ritardato della configurazione delle viste (si modifica spesso: un colpo ogni mezzo secondo).
  const viewsRef = useRef([]);
  const saveTimers = useRef({});
  useEffect(() => {
    viewsRef.current = views;
  }, [views]);
  useEffect(() => () => Object.values(saveTimers.current).forEach(clearTimeout), []);

  const load = useCallback(async () => {
    try {
      const data = await getProject(projectId, lang);
      setProject(data.project);
      setColumns(data.columns);
      setTasks(data.tasks);
      setViews(data.views);
      setTaskTemplates(data.task_templates || []);
      setActiveViewId((current) => {
        if (current && data.views.some((v) => v.id === current)) return current;
        const stored = readStoredView(projectId);
        return data.views.some((v) => v.id === stored) ? stored : (data.views[0]?.id ?? null);
      });
      setError('');
      const wanted = Number(searchParams.get('task'));
      if (wanted && data.tasks.some((task) => task.id === wanted)) {
        setSelectedTaskId(wanted);
        setSearchParams({}, { replace: true, state: location.state }); // l'origine (es. il ticket) si conserva
      }
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.loadError')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    setProject(null);
    setSelectedTaskId(null);
    setActiveViewId(null);
    setSearch('');
    setBoardMenu(false);
    setSelectedIds(new Set());
    load();
  }, [load]);

  useEffect(() => {
    if (location.state && location.state.importCsv) {
      setImportState({ text: location.state.importCsv, name: location.state.importName || 'import.csv' });
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    getUsers()
      .then((d) => {
        // Si segna l'utente corrente (compare come "(tu)"), e lo si aggiunge se chi guarda non può elencare gli utenti.
        const list = (d.users || []).map((u) => ({ ...u, isMe: u.id === user.id }));
        if (!list.some((u) => u.isMe)) list.push({ id: user.id, name: user.name, surname: user.surname || '', isMe: true });
        setUsers(list);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Errore di salvataggio: si ricarica dal server (annulla la modifica ottimistica) e si mostra il messaggio.
  const fail = async (err, fallbackKey) => {
    await load();
    setError(errorMessage(err, t(fallbackKey)));
  };

  const columnsById = useMemo(() => Object.fromEntries(columns.map((c) => [String(c.id), c])), [columns]);
  const activeView = views.find((v) => v.id === activeViewId) || views[0] || null;
  const config = activeView ? activeView.config : {};
  const viewType = activeView ? activeView.type : 'table';

  const statusColumn = useMemo(() => columns.find((c) => c.type === 'status'), [columns]);
  const doneCount = useMemo(() => {
    if (!statusColumn) return null;
    const doneValues = statusColumn.options.filter((o) => o.done).map((o) => o.value);
    if (doneValues.length === 0) return null;
    return tasks.filter((task) => doneValues.includes(task.data[statusColumn.id])).length;
  }, [statusColumn, tasks]);

  // Attività visibili nella vista: filtro, ricerca, ordinamento.
  const visibleTasks = useMemo(() => {
    const ctx = { userId: user.id };
    const pseudo = { ...columnsById, [TICKETS_COLUMN]: ticketsPseudoColumn('') };
    const filtered = applyFilter(tasks, config.filter, pseudo, ctx);
    // Si cerca solo nelle colonne che si vedono (non in quelle nascoste).
    const searchColumns = columns.filter((column) => !(config.hidden || []).includes(column.id));
    const searched = applySearch(filtered, search, searchColumns, users);
    return applySorts(searched, config.sorts, columnsById, users);
  }, [tasks, config.filter, config.sorts, config.hidden, search, columns, columnsById, users, user.id]);

  // Raggruppamento: la tabella lo ha facoltativo, la board lo richiede (di default la prima colonna di stato).
  const groupColumn = useMemo(() => {
    const chosen = config.group ? columnsById[String(config.group)] : null;
    if (chosen && GROUPABLE_TYPES.includes(chosen.type)) return chosen;
    if (viewType === 'board') return columns.find((c) => c.type === 'status') || columns.find((c) => c.type === 'select') || null;
    return null;
  }, [config.group, columnsById, columns, viewType]);
  const tableGroups = (viewType === 'table' || viewType === 'list') && groupColumn ? groupTasks(visibleTasks, groupColumn, users) : null;

  // Gantt: colonne data per inizio e fine (di default le prime due).
  const dateColumns = useMemo(() => columns.filter((c) => c.type === 'date' || c.type === 'daterange'), [columns]);
  const ganttConf = config.gantt || {};
  const ganttStart = dateColumns.find((c) => c.id === ganttConf.start) || dateColumns[0] || null;
  const ganttEnd = ganttStart?.type === 'daterange' || ganttConf.end === 0
    ? null
    : dateColumns.find((c) => c.id === ganttConf.end && c.id !== ganttStart?.id)
      || dateColumns.find((c) => c.id !== ganttStart?.id && c.type === 'date')
      || null;
  const ganttZoom = ganttConf.zoom || 'day';
  // Dipendenze: colonna "relazione con attività" che indica le attività precedenti (di default la prima).
  const relationColumns = useMemo(() => columns.filter((c) => c.type === 'task_relation'), [columns]);
  const dependencyColumn = ganttConf.dependencies === 0 ? null : (relationColumns.find((c) => c.id === ganttConf.dependencies) || relationColumns[0] || null);

  // Galleria: colonna di file da cui prendere l'immagine di copertina.
  const fileColumns = useMemo(() => columns.filter((c) => c.type === 'files'), [columns]);
  const galleryConf = config.gallery || {};
  const coverColumn = galleryConf.cover === 0 ? null : (fileColumns.find((c) => c.id === galleryConf.cover) || fileColumns[0] || null);

  // Calendario: colonna data (o intervallo) da mostrare.
  const calendarColumn = dateColumns.find((c) => c.id === config.calendar?.date) || dateColumns[0] || null;

  const selectedTask = tasks.find((task) => task.id === selectedTaskId) || null;

  // --- Viste ---

  const persistConfig = (viewId, nextConfig) => {
    clearTimeout(saveTimers.current[viewId]);
    saveTimers.current[viewId] = setTimeout(() => {
      updateProjectView(viewId, { config: nextConfig }).catch((err) => setError(errorMessage(err, t('projectView.saveError'))));
    }, 450);
  };

  const patchConfig = (patch) => {
    if (!activeView) return;
    const current = viewsRef.current.find((v) => v.id === activeView.id);
    const next = { ...current.config, ...patch };
    const updated = viewsRef.current.map((v) => (v.id === activeView.id ? { ...v, config: next } : v));
    viewsRef.current = updated;
    setViews(updated);
    persistConfig(activeView.id, next);
  };

  const selectView = (viewId) => {
    setActiveViewId(viewId);
    writeStoredView(projectId, viewId);
    setBoardMenu(false);
  };

  // Configurazione di partenza di una vista nuova, in base al tipo.
  const initialConfig = (type) => {
    if (type === 'board') {
      const group = columns.find((c) => c.type === 'status') || columns.find((c) => c.type === 'select');
      return group ? { group: group.id } : {};
    }
    if (type === 'gantt') return { gantt: { start: dateColumns[0]?.id, end: dateColumns[1]?.id ?? 0, zoom: 'day' } };
    if (type === 'calendar') return dateColumns[0] ? { calendar: { date: dateColumns[0].id } } : {};
    if (type === 'gallery') return fileColumns[0] ? { gallery: { cover: fileColumns[0].id } } : {};
    return {};
  };

  const createView = async (type) => {
    try {
      const data = await createProjectView(project.id, { name: t(`projectView.type_${type}`), type, config: initialConfig(type) });
      setViews((prev) => [...prev, data.view]);
      selectView(data.view.id);
    } catch (err) {
      setError(errorMessage(err, t('projectView.saveError')));
    }
  };

  const renameView = async (view, name) => {
    setViews((prev) => prev.map((v) => (v.id === view.id ? { ...v, name } : v)));
    try {
      await updateProjectView(view.id, { name });
    } catch (err) {
      fail(err, 'projectView.saveError');
    }
  };

  const duplicateView = async (view) => {
    try {
      const data = await createProjectView(project.id, { name: `${view.name} (2)`, type: view.type, config: view.config });
      setViews((prev) => [...prev, data.view]);
      selectView(data.view.id);
    } catch (err) {
      setError(errorMessage(err, t('projectView.saveError')));
    }
  };

  const removeView = async (view) => {
    if (!window.confirm(t('projectView.deleteViewConfirm', { name: view.name }))) return;
    try {
      await deleteProjectView(view.id);
      const rest = views.filter((v) => v.id !== view.id);
      setViews(rest);
      if (activeViewId === view.id) selectView(rest[0].id);
    } catch (err) {
      setError(errorMessage(err, t('projectView.saveError')));
    }
  };

  // --- Progetto ---

  const saveProject = async (patch) => {
    const next = { ...project, ...patch };
    if (!next.name.trim()) return;
    setProject(next);
    try {
      await updateProject(project.id, { name: next.name, description: next.description, icon: next.icon || '', cover: next.cover || '' });
      if (patch.name !== undefined || patch.icon !== undefined) {
        setProjectMenu(getProjectMenu().map((p) => (p.id === project.id ? { ...p, name: next.name.trim(), icon: next.icon || null } : p)));
      }
    } catch (err) {
      fail(err, 'projectDetail.saveError');
    }
  };

  const toggleFavorite = async () => {
    const next = !project.is_favorite;
    setProject({ ...project, is_favorite: next });
    setProjectMenu(getProjectMenu().map((p) => (p.id === project.id ? { ...p, favorite: next } : p)));
    try {
      await setProjectFavorite(project.id, next);
    } catch (err) {
      fail(err, 'projectDetail.saveError');
    }
  };

  const handleDuplicateProject = async () => {
    try {
      const data = await duplicateProject(project.id);
      setProjectMenu((await getProjects()).projects || []);
      navigate(`/projects/${data.project.id}`);
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm(t('projectDetail.deleteProjectConfirm', { name: project.name }))) return;
    try {
      await deleteProject(project.id);
      setProjectMenu(getProjectMenu().filter((p) => p.id !== project.id));
      navigate('/projects');
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.deleteError')));
    }
  };

  // --- Attività ---

  // Cronologia annulla/ripeti (Ctrl+Z, Ctrl+Maiusc+Z / Ctrl+Y fuori dai campi di testo).
  const history = useRef(createHistory()).current;
  const [, setHistoryTick] = useState(0);
  const record = (entry) => {
    history.push(entry);
    setHistoryTick((n) => n + 1);
  };
  const runHistory = async (direction) => {
    try {
      await (direction === 'undo' ? history.undo() : history.redo());
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
    }
    setHistoryTick((n) => n + 1);
    await load();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      const el = e.target;
      const editing = el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'file'].includes(el.type)) || el.isContentEditable);
      if (editing) return;
      e.preventDefault();
      runHistory(key === 'y' || e.shiftKey ? 'redo' : 'undo');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Avviso temporaneo con "Annulla" (es. dopo aver spostato attività nel cestino).
  const noticeTimer = useRef(null);
  const showNotice = (text, canUndo = true) => {
    clearTimeout(noticeTimer.current);
    setNotice({ text, canUndo });
    noticeTimer.current = setTimeout(() => setNotice(null), 7000);
  };
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  // Quando la vista è ordinata, filtrata o cercata le attività non si riordinano né si mostrano ad albero.
  const hasFilterOrSort = (config.filter?.rules?.length > 0) || (config.sorts?.length > 0) || Boolean(search);
  const canReorder = viewType === 'table' && !(config.sorts?.length > 0);
  const treeMode = viewType === 'table' && !hasFilterOrSort && !tableGroups;

  // `groupValue`: dalla board o da una sezione, la nuova attività nasce con quel valore di raggruppamento.
  // extra: { parentId, templateId, data, open }.
  const addTask = async (groupValue = null, extra = {}) => {
    try {
      const body = { title: '' };
      if (extra.parentId) body.parent_id = extra.parentId;
      if (extra.templateId) body.template_id = extra.templateId;
      const data = { ...(extra.data || {}) };
      if (groupColumn && groupValue !== null && groupValue !== undefined) data[String(groupColumn.id)] = groupValue;
      if (Object.keys(data).length > 0) body.data = data;
      const res = await createProjectTask(project.id, body);
      const newId = res.task.id;
      setTasks((prev) => [...prev, res.task]);
      record({ undo: () => deleteProjectTask(newId), redo: () => restoreProjectTask(newId) });
      if (viewType === 'board' || viewType === 'calendar' || extra.open || extra.templateId) setSelectedTaskId(newId);
      else setFocusTaskId(newId);
      return res.task;
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
      return null;
    }
  };

  // Aggiornamento ottimistico: la cella mostra subito il nuovo valore e il salvataggio avviene dietro.
  const patchTask = async (taskId, { title, data }, options = {}) => {
    const before = tasks.find((x) => x.id === taskId);
    if (before && options.record !== false) {
      const previous = {
        ...(title !== undefined ? { title: before.title } : {}),
        data: Object.fromEntries(Object.keys(data || {}).map((key) => [key, before.data[key] ?? null])),
      };
      const next = { ...(title !== undefined ? { title } : {}), data: data || {} };
      record({ undo: () => updateProjectTask(taskId, previous), redo: () => updateProjectTask(taskId, next) });
    }
    setTasks((prev) => prev.map((task) => {
      if (task.id !== taskId) return task;
      const nextData = { ...task.data };
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value === null || value === '') delete nextData[key];
        else nextData[key] = value;
      });
      return { ...task, title: title !== undefined ? title : task.title, data: nextData };
    }));
    try {
      await updateProjectTask(taskId, { ...(title !== undefined ? { title } : {}), ...(data ? { data } : {}) });
    } catch (err) {
      fail(err, 'projectDetail.saveError');
    }
  };

  // Elimina (nel cestino, con le sotto-attività): si può annullare subito o ripristinare dal cestino.
  const deleteTasks = async (ids) => {
    if (ids.length === 0) return;
    const roots = ids.filter((id) => !ids.includes(tasks.find((x) => x.id === id)?.parent_id));
    try {
      const res = await bulkProjectTasks(project.id, ids, 'delete');
      const trashed = new Set(res.trashed_ids);
      setTasks((prev) => prev.filter((x) => !trashed.has(x.id)));
      setSelectedIds(new Set());
      setSelectedTaskId(null);
      record({
        undo: async () => { for (const id of roots) await restoreProjectTask(id).catch(() => {}); },
        redo: () => bulkProjectTasks(project.id, ids, 'delete'),
      });
      showNotice(t('projectDetail.movedToTrash', { count: ids.length }));
    } catch (err) {
      fail(err, 'projectDetail.deleteError');
    }
  };

  const handleDeleteTask = (task) => deleteTasks([task.id]);

  const duplicateTasks = async (ids) => {
    try {
      const res = ids.length === 1 ? await duplicateProjectTask(ids[0]) : await bulkProjectTasks(project.id, ids, 'duplicate');
      const newIds = res.tasks.map((x) => x.id);
      await load();
      setSelectedIds(new Set());
      record({
        undo: () => bulkProjectTasks(project.id, newIds, 'delete'),
        redo: async () => { for (const id of newIds) await restoreProjectTask(id).catch(() => {}); },
      });
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  // Stesso valore di una colonna su tutte le attività selezionate.
  const bulkSet = async (columnId, value) => {
    const ids = [...selectedIds];
    const key = String(columnId);
    const previous = Object.fromEntries(ids.map((id) => [id, tasks.find((x) => x.id === id)?.data[key] ?? null]));
    try {
      const res = await bulkProjectTasks(project.id, ids, 'set', { [key]: value });
      const updated = new Map(res.tasks.map((x) => [x.id, x]));
      setTasks((prev) => prev.map((x) => (updated.has(x.id) ? { ...updated.get(x.id), tickets: x.tickets } : x)));
      record({
        undo: () => Promise.all(ids.map((id) => updateProjectTask(id, { data: { [key]: previous[id] } }))),
        redo: () => bulkProjectTasks(project.id, ids, 'set', { [key]: value }),
      });
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  const toggleSelect = (id, range) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (range && lastSelected.current !== null) {
        const order = visibleTasks.map((x) => x.id);
        const from = order.indexOf(lastSelected.current);
        const to = order.indexOf(id);
        if (from >= 0 && to >= 0) order.slice(Math.min(from, to), Math.max(from, to) + 1).forEach((x) => next.add(x));
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    lastSelected.current = id;
  };

  const toggleAll = (ids, checked) => setSelectedIds(checked ? new Set(ids) : new Set());

  // Trascinamento di una riga: prima/dopo un'altra (con le sue sotto-attività). Nell'albero prende il padre
  // della riga di destinazione; in una tabella raggruppata, il valore del gruppo di destinazione.
  const reorderTasks = async (draggedId, targetId, where) => {
    const dragged = tasks.find((x) => x.id === draggedId);
    const target = tasks.find((x) => x.id === targetId);
    if (!dragged || !target) return;
    const before = tasks.map((x) => x.id);
    const subtree = descendantIds(tasks, draggedId);
    const rest = tasks.filter((x) => !subtree.has(x.id));
    let index = rest.findIndex((x) => x.id === targetId);
    if (where === 'after') {
      const targetTree = descendantIds(tasks, targetId);
      let last = index;
      rest.forEach((x, i) => { if (targetTree.has(x.id)) last = Math.max(last, i); });
      index = last + 1;
    }
    const moved = tasks.filter((x) => subtree.has(x.id));
    const ordered = [...rest.slice(0, index), ...moved, ...rest.slice(index)];

    const oldParent = dragged.parent_id;
    const newParent = treeMode ? target.parent_id : oldParent;
    const groupKey = groupColumn && viewType === 'table' ? String(groupColumn.id) : null;
    // Raggruppando per persona le sezioni sono per la prima persona: si sposta su quella della sezione di arrivo.
    const isPersonGroup = groupColumn?.type === 'person';
    const firstPerson = (task) => personIds(task.data[groupKey])[0] ?? null;
    const oldGroup = groupKey ? (dragged.data[groupKey] ?? null) : null;
    const newGroup = groupKey
      ? (isPersonGroup ? (firstPerson(target) === null ? null : [firstPerson(target)]) : (target.data[groupKey] ?? null))
      : null;

    setTasks(ordered.map((x) => {
      if (x.id !== draggedId) return x;
      const nextData = { ...x.data };
      if (groupKey) {
        if (newGroup === null) delete nextData[groupKey];
        else nextData[groupKey] = newGroup;
      }
      return { ...x, parent_id: newParent, data: nextData };
    }));

    const apply = async (parent, group, order) => {
      if (parent !== oldParent || parent !== newParent) await updateProjectTask(draggedId, { parent_id: parent });
      if (groupKey) await updateProjectTask(draggedId, { data: { [groupKey]: group } });
      await reorderProjectTasks(project.id, order);
    };
    try {
      await apply(newParent, newGroup, ordered.map((x) => x.id));
      record({
        undo: () => apply(oldParent, oldGroup, before),
        redo: () => apply(newParent, newGroup, ordered.map((x) => x.id)),
      });
    } catch (err) {
      fail(err, 'projectDetail.saveError');
    }
  };

  // Scheda a colonne della board: sposta la card in un'altra colonna e/o prima di un'altra card.
  const moveCard = async (draggedId, targetId, groupValue) => {
    const dragged = tasks.find((x) => x.id === draggedId);
    if (!dragged || !groupColumn) return;
    const key = String(groupColumn.id);
    const oldGroup = dragged.data[key] ?? null;
    const before = tasks.map((x) => x.id);
    let ordered = before;
    if (targetId !== null && targetId !== draggedId) {
      const rest = before.filter((id) => id !== draggedId);
      rest.splice(rest.indexOf(targetId), 0, draggedId);
      ordered = rest;
    }
    setTasks(ordered.map((id) => {
      const x = tasks.find((task) => task.id === id);
      if (id !== draggedId) return x;
      const nextData = { ...x.data };
      if (groupValue === null) delete nextData[key];
      else nextData[key] = groupValue;
      return { ...x, data: nextData };
    }));
    const apply = async (group, order) => {
      await updateProjectTask(draggedId, { data: { [key]: group } });
      if (order) await reorderProjectTasks(project.id, order);
    };
    try {
      await apply(groupValue, targetId !== null ? ordered : null);
      record({ undo: () => apply(oldGroup, before), redo: () => apply(groupValue, targetId !== null ? ordered : null) });
    } catch (err) {
      fail(err, 'projectDetail.saveError');
    }
  };

  // Incolla da Excel: righe e colonne a partire dalla cella scelta; oltre l'ultima riga se ne creano di nuove.
  const pasteTable = async (startTaskId, startColumn, matrix) => {
    const displayed = [...document.querySelectorAll('.pd-table tbody tr[data-task-id]')].map((tr) => Number(tr.dataset.taskId));
    const startIndex = displayed.indexOf(startTaskId);
    if (startIndex < 0) return;
    const cols = orderedColumns(columns, config);
    const rows = matrix.map((cells, r) => {
      const taskId = displayed[startIndex + r];
      const mapped = {};
      cells.forEach((text, c) => {
        const column = cols[startColumn + c];
        if (column) mapped[column.type === 'title' ? 'title' : String(column.id)] = text;
      });
      return { ...(taskId ? { task_id: taskId } : {}), cells: mapped };
    });
    const snapshots = rows.filter((row) => row.task_id).map((row) => {
      const task = tasks.find((x) => x.id === row.task_id);
      return { id: task.id, title: task.title, data: { ...task.data } };
    });
    try {
      const res = await pasteProjectTasks(project.id, rows, false);
      const known = new Set(tasks.map((x) => x.id));
      const createdIds = res.tasks.map((x) => x.id).filter((id) => !known.has(id));
      const after = new Map(res.tasks.map((x) => [x.id, x]));
      await load();
      record({
        undo: async () => {
          for (const snap of snapshots) {
            const current = after.get(snap.id);
            const keys = new Set([...Object.keys(snap.data), ...Object.keys(current ? current.data : {})]);
            await updateProjectTask(snap.id, { title: snap.title, data: Object.fromEntries([...keys].map((k) => [k, snap.data[k] ?? null])) });
          }
          if (createdIds.length > 0) await bulkProjectTasks(project.id, createdIds, 'delete');
        },
        redo: async () => {
          for (const id of createdIds) await restoreProjectTask(id).catch(() => {});
          const existing = rows.filter((row) => row.task_id);
          if (existing.length > 0) await pasteProjectTasks(project.id, existing, false);
        },
      });
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  // --- Modelli di riga ---

  const saveTemplate = async (task) => {
    const name = window.prompt(t('projectDetail.templateNamePrompt'), task.title || '');
    if (name === null) return;
    try {
      const data = await createProjectTaskTemplate(project.id, task.id, name);
      setTaskTemplates((prev) => [...prev, data.template]);
      showNotice(t('projectDetail.templateSaved', { name: data.template.name }), false);
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  const renameTemplate = async (template) => {
    const name = window.prompt(t('projectDetail.templateNamePrompt'), template.name);
    if (!name || !name.trim()) return;
    try {
      const data = await renameProjectTaskTemplate(template.id, name.trim());
      setTaskTemplates((prev) => prev.map((x) => (x.id === template.id ? data.template : x)));
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  const removeTemplate = async (template) => {
    if (!window.confirm(t('projectDetail.deleteTemplateConfirm', { name: template.name }))) return;
    try {
      await deleteProjectTaskTemplate(template.id);
      setTaskTemplates((prev) => prev.filter((x) => x.id !== template.id));
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.deleteError')));
    }
  };

  const rowAction = (action, task) => {
    if (action === 'open') setSelectedTaskId(task.id);
    else if (action === 'subtask') addTask(null, { parentId: task.id });
    else if (action === 'duplicate') duplicateTasks([task.id]);
    else if (action === 'template') saveTemplate(task);
    else if (action === 'delete') deleteTasks([task.id]);
  };

  // --- Esporta / importa CSV ---

  const exportCsv = () => {
    const rows = buildExportRows(columns, config, visibleTasks, users, tasks, t('projectDetail.ticketsColumn'));
    const slug = project.name.trim().replace(/[^a-zA-Z0-9\u00C0-\u017F _-]+/g, '').replace(/\s+/g, '-') || 'progetto';
    downloadText(`${slug}.csv`, toCsv(rows));
  };

  const pickCsv = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv,text/plain';
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      setImportState({ text: await file.text(), name: file.name });
    };
    input.click();
  };

  const setTaskTickets = (taskId, tickets) => setTasks((prev) => prev.map((task) => (task.id === taskId ? { ...task, tickets } : task)));

  // Gantt: sposta/ridimensiona una barra scrivendo le date (la fine solo se la colonna c'è ed è coinvolta).
  const shiftTask = (task, startIso, endIso, writeEnd) => patchTask(task.id, {
    data: ganttStart.type === 'daterange'
      ? { [String(ganttStart.id)]: { start: startIso, end: writeEnd ? endIso : undefined } }
      : {
        [String(ganttStart.id)]: startIso,
        ...(ganttEnd && writeEnd ? { [String(ganttEnd.id)]: endIso } : {}),
      },
  });

  // Calendario: sposta l'attività di `delta` giorni (entrambe le date se è un intervallo).
  const moveCalendarTask = (taskId, delta, toIso) => {
    const task = tasks.find((x) => x.id === taskId);
    if (!task || !calendarColumn) return;
    const shiftIso = (iso) => {
      const [y, m, d] = iso.split('-').map(Number);
      return toIso(Math.round(Date.UTC(y, m - 1, d) / 86400000) + delta);
    };
    const raw = task.data[String(calendarColumn.id)];
    if (calendarColumn.type === 'daterange') {
      patchTask(taskId, { data: { [String(calendarColumn.id)]: { start: raw?.start ? shiftIso(raw.start) : undefined, end: raw?.end ? shiftIso(raw.end) : undefined } } });
    } else if (raw) {
      patchTask(taskId, { data: { [String(calendarColumn.id)]: shiftIso(raw) } });
    }
  };

  const addTaskOnDay = async (iso) => {
    try {
      const value = calendarColumn.type === 'daterange' ? { start: iso } : iso;
      const data = await createProjectTask(project.id, { title: '', data: { [String(calendarColumn.id)]: value } });
      setTasks((prev) => [...prev, data.task]);
      setSelectedTaskId(data.task.id);
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  // Sostituisce un'attività con quella restituita dal server (file caricati/eliminati).
  const replaceTask = (updated) => setTasks((prev) => prev.map((task) => (task.id === updated.id ? { ...updated, tickets: task.tickets } : task)));

  // --- Colonne ---

  // `position` ({anchor, side}): la colonna nuova va a sinistra o a destra di un'altra, non in fondo.
  const submitColumn = async (column, payload, position = null) => {
    try {
      if (column === null) {
        const data = await createProjectColumn(project.id, payload);
        setColumns((prev) => [...prev, data.column]);
        if (position) {
          const others = allColumnsInOrder(columns, config).filter((c) => c.type !== 'title').map((c) => c.id);
          const anchorIndex = others.indexOf(position.anchor); // -1 se l'ancora è la colonna del titolo
          const at = anchorIndex === -1 ? 0 : anchorIndex + (position.side === 'right' ? 1 : 0);
          others.splice(at, 0, data.column.id);
          patchConfig({ order: others });
        }
      } else {
        await updateProjectColumn(column.id, payload);
        // Opzioni eliminate e cambi di tipo modificano anche le celle: si riallinea tutto dal server.
        await load();
      }
      setError('');
    } catch (err) {
      setError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  const removeColumn = async (column) => {
    if (!window.confirm(t('projectDetail.deleteColumnConfirm', { name: column.name }))) return;
    setColumns((prev) => prev.filter((c) => c.id !== column.id));
    try {
      await deleteProjectColumn(column.id);
    } catch (err) {
      fail(err, 'projectDetail.deleteError');
    }
  };

  const columnAction = async (action, column, extra) => {
    if (action === 'sort') patchConfig({ sorts: [{ column: column.id, dir: extra }] });
    else if (action === 'hide') patchConfig({ hidden: [...(config.hidden || []), column.id] });
    else if (action === 'group') patchConfig({ group: column.id });
    else if (action === 'filter') {
      const rules = config.filter?.rules || [];
      patchConfig({ filter: { mode: config.filter?.mode || 'and', rules: [...rules, { column: column.id, op: opsForColumn(column)[0], value: '' }] } });
      setForceOpen({ panel: 'filter', nonce: Date.now() });
    } else if (action === 'duplicate') {
      try {
        const data = await duplicateProjectColumn(column.id);
        setColumns((prev) => [...prev, data.column]);
        await load();
      } catch (err) {
        setError(errorMessage(err, t('projectDetail.saveError')));
      }
    } else if (action === 'delete') removeColumn(column);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="nt-page"><div className="loading">{t('common.loading')}</div></div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="nt-page">
          {error && <div className="error-message">{error}</div>}
          <Link to="/projects" className="nt-ghost-btn">{t('projectDetail.back')}</Link>
        </div>
      </Layout>
    );
  }

  const noGroupHint = viewType === 'board' && !groupColumn;
  const noDateHint = (viewType === 'gantt' || viewType === 'calendar') && dateColumns.length === 0;

  // Se si arriva dal dettaglio di un ticket (attività collegata), si può tornare lì.
  const ticketFrom = typeof location.state?.from === 'string' && /^\/tickets\/\d+$/.test(location.state.from) ? location.state.from : null;
  const backToTicket = ticketFrom ? { label: t('projectDetail.backToTicket', { id: ticketFrom.split('/').pop() }), go: () => navigate(ticketFrom) } : null;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        {backToTicket && (
          <button className="tpl-back" type="button" onClick={backToTicket.go}>← {backToTicket.label}</button>
        )}
        <ProjectHeader project={project} onSave={saveProject} onDuplicate={handleDuplicateProject} onDelete={handleDeleteProject} onExport={exportCsv} onImport={pickCsv} onTrash={() => setTrashOpen(true)} onToggleFavorite={toggleFavorite} />

        <div className="nt-tabs">
          <ProjectViewTabs
            views={views}
            activeId={activeView?.id}
            onSelect={selectView}
            onCreate={createView}
            onRename={renameView}
            onDuplicate={duplicateView}
            onDelete={removeView}
          />
          <div className="nt-tabs-actions">
            {viewType === 'gantt' && ganttStart && (
              <span className="gantt-controls">
                <Dropdown
                  value={ganttStart.id}
                  ariaLabel={t('projectDetail.ganttStart')}
                  options={dateColumns.map((c) => ({ value: c.id, label: t('projectDetail.ganttStartOption', { name: c.name }) }))}
                  onChange={(v) => patchConfig({ gantt: { ...ganttConf, start: Number(v) } })}
                />
                <ColumnMenuButton column={ganttStart} onColumnSubmit={submitColumn} onColumnAction={columnAction} />
                {ganttStart.type === 'date' && dateColumns.filter((c) => c.type === 'date').length > 1 && (
                  <Dropdown
                    value={ganttEnd ? ganttEnd.id : 0}
                    ariaLabel={t('projectDetail.ganttEnd')}
                    options={[{ value: 0, label: t('projectDetail.ganttNoEnd') }, ...dateColumns.filter((c) => c.id !== ganttStart.id && c.type === 'date').map((c) => ({ value: c.id, label: t('projectDetail.ganttEndOption', { name: c.name }) }))]}
                    onChange={(v) => patchConfig({ gantt: { ...ganttConf, end: Number(v) } })}
                  />
                )}
                {ganttEnd && <ColumnMenuButton column={ganttEnd} onColumnSubmit={submitColumn} onColumnAction={columnAction} />}
                {relationColumns.length > 0 && (
                  <Dropdown
                    value={dependencyColumn ? dependencyColumn.id : 0}
                    ariaLabel={t('projectDetail.ganttDependencies')}
                    options={[{ value: 0, label: t('projectDetail.ganttNoDeps') }, ...relationColumns.map((c) => ({ value: c.id, label: t('projectDetail.ganttDepsOption', { name: c.name }) }))]}
                    onChange={(v) => patchConfig({ gantt: { ...ganttConf, dependencies: Number(v) } })}
                  />
                )}
                {dependencyColumn && <ColumnMenuButton column={dependencyColumn} onColumnSubmit={submitColumn} onColumnAction={columnAction} />}
                <Dropdown
                  value={ganttZoom}
                  ariaLabel={t('projectDetail.ganttZoom')}
                  options={['day', 'week', 'month', 'quarter'].map((z) => ({ value: z, label: t(`projectDetail.zoom_${z}`) }))}
                  onChange={(zoom) => patchConfig({ gantt: { ...ganttConf, zoom } })}
                />
              </span>
            )}
            {viewType === 'gallery' && fileColumns.length > 0 && (
              <span className="gantt-controls">
                <Dropdown
                  value={coverColumn ? coverColumn.id : 0}
                  ariaLabel={t('projectView.galleryCover')}
                  options={[{ value: 0, label: t('projectView.galleryNoCover') }, ...fileColumns.map((c) => ({ value: c.id, label: t('projectView.galleryCoverOption', { name: c.name }) }))]}
                  onChange={(v) => patchConfig({ gallery: { ...galleryConf, cover: Number(v) } })}
                />
              </span>
            )}
            {viewType === 'calendar' && calendarColumn && (
              <span className="gantt-controls">
                {dateColumns.length > 1 && (
                  <Dropdown
                    value={calendarColumn.id}
                    ariaLabel={t('projectView.calendarDate')}
                    options={dateColumns.map((c) => ({ value: c.id, label: t('projectView.calendarDateOption', { name: c.name }) }))}
                    onChange={(v) => patchConfig({ calendar: { ...(config.calendar || {}), date: Number(v) } })}
                  />
                )}
                <ColumnMenuButton column={calendarColumn} onColumnSubmit={submitColumn} onColumnAction={columnAction} />
              </span>
            )}
            {viewType === 'board' && groupColumn && OPTION_TYPES.includes(groupColumn.type) && (
              <span className="pd-board-menu">
                <button className="nt-ghost-btn" type="button" onClick={() => setBoardMenu(true)}>{t('projectDetail.editOptions')}</button>
                {boardMenu && (
                  <ColumnMenuHost
                    column={groupColumn}
                    initialView="edit"
                    onClose={() => setBoardMenu(false)}
                    onColumnSubmit={submitColumn}
                    onColumnAction={columnAction}
                  />
                )}
              </span>
            )}
            {doneCount !== null && (
              <span className="nt-muted pd-progress">{t('projectDetail.progress', { done: doneCount, total: tasks.length })}</span>
            )}
            <ViewToolbar
              viewType={viewType}
              columns={columns}
              users={users}
              config={config}
              onConfig={patchConfig}
              onColumnSubmit={submitColumn}
              onColumnAction={columnAction}
              search={search}
              onSearch={setSearch}
              forceOpen={forceOpen}
            />
            <span className="pd-undo">
              <button type="button" className="nt-ghost-btn" disabled={!history.canUndo()} onClick={() => runHistory('undo')} aria-label={t('projectDetail.undo')} title={t('projectDetail.undo')}>↶</button>
              <button type="button" className="nt-ghost-btn" disabled={!history.canRedo()} onClick={() => runHistory('redo')} aria-label={t('projectDetail.redo')} title={t('projectDetail.redo')}>↷</button>
            </span>
            <NewTaskSplit
              templates={taskTemplates}
              onNew={() => addTask()}
              onApply={(template) => addTask(null, { templateId: template.id })}
              onRename={renameTemplate}
              onDelete={removeTemplate}
            />
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {selectedIds.size > 0 && viewType === 'table' && (
          <BulkBar
            count={selectedIds.size}
            columns={columns}
            users={users}
            allTasks={tasks}
            onDuplicate={() => duplicateTasks([...selectedIds])}
            onDelete={() => deleteTasks([...selectedIds])}
            onSet={bulkSet}
            onClear={() => setSelectedIds(new Set())}
          />
        )}
        {noGroupHint && <p className="nt-muted">{t('projectView.needSelectColumn')}</p>}
        {noDateHint && <p className="nt-muted">{t('projectView.needDateColumn')}</p>}
        {(config.filter?.rules?.length > 0 || search) && (
          <p className="nt-muted pv-count">{t('projectView.showing', { shown: visibleTasks.length, total: tasks.length })}</p>
        )}

        {viewType === 'calendar' && calendarColumn ? (
          <ProjectCalendar
            tasks={visibleTasks}
            dateColumn={calendarColumn}
            statusColumn={statusColumn}
            color={project.color}
            onOpen={(task) => setSelectedTaskId(task.id)}
            onMove={moveCalendarTask}
            onAdd={addTaskOnDay}
          />
        ) : viewType === 'gantt' && ganttStart ? (
          <ProjectGantt
            tasks={visibleTasks}
            startColumn={ganttStart}
            endColumn={ganttEnd}
            dependencyColumn={dependencyColumn}
            statusColumn={statusColumn}
            color={project.color}
            zoom={ganttZoom}
            onOpen={(task) => setSelectedTaskId(task.id)}
            onShift={shiftTask}
          />
        ) : viewType === 'board' && groupColumn ? (
          <ProjectBoard
            columns={columns}
            tasks={visibleTasks}
            groupColumn={groupColumn}
            users={users}
            onAdd={addTask}
            onMoveCard={moveCard}
            onOpen={(task) => setSelectedTaskId(task.id)}
          />
        ) : viewType === 'list' ? (
          <ProjectList
            tasks={visibleTasks}
            allTasks={tasks}
            columns={orderedColumns(columns, config)}
            users={users}
            groups={tableGroups}
            groupColumn={groupColumn}
            treeMode={viewType === 'list' && !hasFilterOrSort && !tableGroups}
            statusColumn={statusColumn}
            onOpen={(task) => setSelectedTaskId(task.id)}
            onPatchTask={patchTask}
            onAdd={(groupValue) => addTask(groupValue, { open: true })}
          />
        ) : viewType === 'gallery' ? (
          <ProjectGallery
            tasks={visibleTasks}
            allTasks={tasks}
            columns={orderedColumns(columns, config)}
            users={users}
            coverColumn={coverColumn}
            color={project.color}
            onOpen={(task) => setSelectedTaskId(task.id)}
            onAdd={() => addTask(null, { open: true })}
          />
        ) : viewType === 'table' ? (
          <ProjectTable
            columns={columns}
            tasks={visibleTasks}
            allTasks={tasks}
            groups={tableGroups}
            groupColumn={groupColumn}
            treeMode={treeMode}
            config={config}
            users={users}
            focusTaskId={focusTaskId}
            selectedIds={selectedIds}
            canReorder={canReorder}
            onFocusDone={() => setFocusTaskId(null)}
            onPatchTask={patchTask}
            onTaskReplace={replaceTask}
            onError={setError}
            onAddTask={addTask}
            onOpenTask={(task) => setSelectedTaskId(task.id)}
            onToggleSelect={toggleSelect}
            onToggleAll={toggleAll}
            onReorder={reorderTasks}
            onRowAction={rowAction}
            onPasteTable={pasteTable}
            onConfig={patchConfig}
            onColumnSubmit={submitColumn}
            onColumnAction={columnAction}
          />
        ) : null}
      </div>

      {trashOpen && <TrashDialog projectId={project.id} onClose={() => setTrashOpen(false)} onChanged={load} />}
      {importState && (
        <ImportCsvDialog
          projectId={project.id}
          columns={columns}
          text={importState.text}
          fileName={importState.name}
          onClose={() => setImportState(null)}
          onDone={load}
        />
      )}
      {notice && (
        <div className="pd-toast" role="status">
          <span>{notice.text}</span>
          {notice.canUndo && <button type="button" onClick={() => { setNotice(null); runHistory('undo'); }}>{t('projectDetail.undo')}</button>}
        </div>
      )}

      {selectedTask && (
        <TaskPeek
          task={selectedTask}
          allTasks={tasks}
          columns={columns}
          users={users}
          user={user}
          backToTicket={backToTicket}
          onOpenTask={(id) => setSelectedTaskId(id)}
          onAddSubtask={(parentId) => addTask(null, { parentId, open: true })}
          onSetParent={(taskId, parentId) => updateProjectTask(taskId, { parent_id: parentId }).then(load).catch((err) => setError(errorMessage(err, t('projectDetail.saveError'))))}
          onClose={() => setSelectedTaskId(null)}
          onPatch={patchTask}
          onDelete={handleDeleteTask}
          onTaskReplace={replaceTask}
          onTicketsChange={(tickets) => setTaskTickets(selectedTask.id, tickets)}
          onError={setError}
        />
      )}
    </Layout>
  );
}

export default ProjectDetail;
